import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import { AdminButton } from "../../pages/admin/action_engine";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const Modal = styled.div`
  width: min(720px, 92vw);
  max-height: 86vh;
  overflow: auto;
  border-radius: 14px;
  padding: 16px;
  background: rgba(18, 22, 33, 0.98);
  border: 1px solid rgba(255,255,255,0.12);

  &:focus {
    outline: none;
  }

  @media (max-width: 640px) {
    width: calc(100vw - 24px);
    max-height: calc(100vh - 24px);
    padding: 14px;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-top: 10px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 6px;
  }
`;

const Label = styled.label`
  font-size: 12px;
  opacity: 0.8;
  width: 70px;
  flex: 0 0 70px;
  padding-top: 10px;

  @media (max-width: 640px) {
    width: auto;
    flex: none;
    padding-top: 0;
  }
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: inherit;
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;

  &:focus-visible {
    outline: 2px solid rgba(255, 79, 163, 0.9);
    outline-offset: 2px;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 140px;
  resize: vertical;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: inherit;
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;

  &:focus-visible {
    outline: 2px solid rgba(255, 79, 163, 0.9);
    outline-offset: 2px;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 14px;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    flex-direction: column-reverse;

    > button {
      width: 100%;
    }
  }
`;

const Btn = styled(AdminButton)``;

const ErrorMessage = styled.div`
  margin-top: 10px;
`;

const SuccessMessage = styled.div`
  margin-top: 10px;
`;

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : "";
}

function normalizeEmails(raw) {
  const parts = (raw || "")
    .split(/[\n,; \t]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  return Array.from(new Set(parts.map((e) => e.toLowerCase())));
}

export default function EventRegistrationEmailSender({
  eventUuid,
  eventTitle,
  defaultRecipients = [],
  publicBaseUrl,
  trigger,
  onSent,
  ensureEventUuid,      // async () => uuid
  onEventUuidResolved,  // (uuid) => void
}) {
  const [open, setOpen] = useState(false);
  const [toRaw, setToRaw] = useState(defaultRecipients.join("\n"));
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [resolvedUuid, setResolvedUuid] = useState(eventUuid || "");

  const firstFieldRef = useRef(null);
  const dialogTitleId = "event-registration-email-dialog-title";

  useEffect(() => {
    if (eventUuid && eventUuid !== resolvedUuid) setResolvedUuid(eventUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventUuid]);

  useEffect(() => {
    if (!open) return undefined;

    firstFieldRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !sending) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, sending]);

  const ensureUuid = async () => {
    if (resolvedUuid && resolvedUuid !== "create") return resolvedUuid;

    if (!ensureEventUuid) {
      throw new Error("L'évènement n'est pas encore créé (ensureEventUuid manquant).");
    }

    const newUuid = await ensureEventUuid();
    if (!newUuid) throw new Error("Création évènement: UUID manquant.");

    setResolvedUuid(newUuid);
    onEventUuidResolved?.(newUuid);
    return newUuid;
  };

  const buildRegisterUrl = (uuid) => {
    const base = (publicBaseUrl || window.location.origin).replace(/\/$/, "");
    return uuid ? `${base}/event/${uuid}/register` : "";
  };

  const defaultSubject = useMemo(() => {
    const t = (eventTitle || "").trim() || "évènement";
    return `Lien d'inscription – ${t}`;
  }, [eventTitle]);

  const buildDefaultBody = (title, url) => {
    const t = (title || "").trim() || "<Titre de l'évènement>";
    return `Bonjour,

      C'est l'équipe Queen's Games, tu trouveras ci-dessous le lien d'inscription à l'évènement ${t} :

      ${url}

      À bientôt,
      Queen's Games`;
  };

  const openModal = async () => {
    setError("");
    setOkMsg("");

    try {
      await ensureUuid();
    } catch (e) {
      setError(e?.message || "Impossible de créer l'évènement.");
      setOpen(true);
      return;
    }

    setToRaw(defaultRecipients.join("\n"));
    setSubject((s) => (s ? s : defaultSubject));
    setBody((b) => (b ? b : defaultBody));
    setOpen(true);
  };

  const closeModal = () => {
    if (!sending) setOpen(false);
  };

  const send = async () => {
    setError("");
    setOkMsg("");

    let uuidToUse = "";
    try {
      uuidToUse = await ensureUuid();
    } catch (e) {
      return setError(e?.message || "Impossible de créer l'évènement.");
    }

    const to = normalizeEmails(toRaw);
    if (!uuidToUse) return setError("UUID évènement manquant.");
    if (to.length === 0) return setError("Ajoute au moins un destinataire.");
    if (!subject.trim()) return setError("Objet vide.");
    if (!body.trim()) return setError("Message vide.");

    const csrf = getCookie("csrf_access_token");
    if (!csrf) return setError("CSRF manquant (csrf_access_token). Reconnecte-toi puis réessaie.");

    setSending(true);
    try {
      const res = await fetch("/api/email/send-event-registration", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": csrf,
        },
        body: JSON.stringify({
          event_uuid: uuidToUse,
          to_emails: to,
          subject: subject.trim(),
          body: body.trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `Erreur HTTP ${res.status}`);
      }

      const sent = data.sent_to || to;
      setOkMsg(`Email envoyé à ${sent.length} destinataire(s).`);
      onSent?.(sent);
    } catch (e) {
      setError(e?.message || "Erreur lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  const Trigger = trigger ? (
    <span
      onClick={async (e) => {
        e.stopPropagation?.();
        await openModal();
      }}
    >
      {trigger}
    </span>
  ) : (
    <Btn type="button" onClick={openModal}>Envoyer un mail</Btn>
  );

  return (
    <>
      {Trigger}

      {open && (
        <Overlay onClick={closeModal}>
          <Modal
            role="dialog"
            aria-modal="true"
            aria-labelledby={dialogTitleId}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id={dialogTitleId} style={{ marginTop: 0 }}>
              Envoyer le lien d'inscription
            </h3>

            <div style={{ fontSize: 13, opacity: 0.9 }}>
              <div><b>Évènement :</b> {eventTitle || "—"}</div>
              <div style={{ marginTop: 6 }}>
                <b>Lien :</b>{" "}
                <a href={registerUrl} target="_blank" rel="noreferrer">{registerUrl}</a>
              </div>
            </div>

            <Row>
              <Label htmlFor="event-email-recipients">À</Label>
              <Textarea
                id="event-email-recipients"
                ref={firstFieldRef}
                value={toRaw}
                onChange={(e) => setToRaw(e.target.value)}
                placeholder={"emails (1 par ligne, ou séparés par , ; espace)\nex: client@mail.fr"}
              />
            </Row>

            <Row>
              <Label htmlFor="event-email-subject">Objet</Label>
              <Input
                id="event-email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </Row>

            <Row>
              <Label htmlFor="event-email-body">Message</Label>
              <Textarea
                id="event-email-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
            </Row>

            {error && (
              <ErrorMessage role="alert" aria-live="assertive">
                {error}
              </ErrorMessage>
            )}

            {okMsg && (
              <SuccessMessage role="status" aria-live="polite">
                {okMsg}
              </SuccessMessage>
            )}

            <Actions>
              <Btn type="button" onClick={closeModal} disabled={sending}>Fermer</Btn>
              <Btn type="button" onClick={send} disabled={sending}>
                {sending ? "Envoi..." : "Envoyer"}
              </Btn>
            </Actions>
          </Modal>
        </Overlay>
      )}
    </>
  );
}